import {
  CPTSlabForManualOption,
  DeliveryModes,
} from "../../../views/Orders/enums";
import { CPT_SLAB_BADGE_ABBREVIATIONS, LOGISTICS_CPT_SLAB_LABELS_BY_LEVEL } from "../../../constants/logisticsCptSlabs";
import { DeliveryOption } from "../types";

export interface DeliveryOptionSlabSource {
  cptSlab?: string | number | null;
  deliveryMode?: string | null;
}

export type DeliveryOptionInput = Omit<DeliveryOption, "cptSlab"> & {
  cptSlab: number | string;
};

export function normalizeCptSlabValue(
  cptSlab?: string | number | null,
): number | undefined {
  if (cptSlab === undefined || cptSlab === null || cptSlab === "") {
    return undefined;
  }
  const parsed = Number(cptSlab);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function getCptSlabBadgeAbbreviation(
  option: DeliveryOptionSlabSource,
): string | null {
  const slab = normalizeCptSlabValue(option.cptSlab);

  switch (slab) {
    case CPTSlabForManualOption.INSTANT_DELIVERY:
      return CPT_SLAB_BADGE_ABBREVIATIONS.INSTANT;
    case CPTSlabForManualOption.STANDARD_DELIVERY:
      return CPT_SLAB_BADGE_ABBREVIATIONS.STANDARD;
    case CPTSlabForManualOption.SELF_PICKUP:
      return CPT_SLAB_BADGE_ABBREVIATIONS.SELF_PICKUP;
    default:
      break;
  }

  switch (option.deliveryMode) {
    case DeliveryModes.SELF_PICKUP:
      return CPT_SLAB_BADGE_ABBREVIATIONS.SELF_PICKUP;
    default:
      return null;
  }
}

export function getCptSlabFullLabel(
  option: DeliveryOptionSlabSource,
): string | null {
  const slab = normalizeCptSlabValue(option.cptSlab);

  switch (slab) {
    case CPTSlabForManualOption.INSTANT_DELIVERY:
      return LOGISTICS_CPT_SLAB_LABELS_BY_LEVEL["1"];
    case CPTSlabForManualOption.STANDARD_DELIVERY:
      return LOGISTICS_CPT_SLAB_LABELS_BY_LEVEL["2"];
    case CPTSlabForManualOption.SELF_PICKUP:
      return LOGISTICS_CPT_SLAB_LABELS_BY_LEVEL["-2"];
    default:
      break;
  }

  switch (option.deliveryMode) {
    case DeliveryModes.SELF_PICKUP:
      return LOGISTICS_CPT_SLAB_LABELS_BY_LEVEL["-2"];
    default:
      return null;
  }
}

export function shouldShowLeftSlabBadge(
  option: Pick<DeliveryOption, "isSystemGenerated"> & DeliveryOptionSlabSource,
): boolean {
  if (option.isSystemGenerated === false) {
    return getCptSlabBadgeAbbreviation(option) != null;
  }

  const slab = normalizeCptSlabValue(option.cptSlab);
  switch (slab) {
    case CPTSlabForManualOption.SELF_PICKUP:
      return true;
    default:
      break;
  }

  return option.deliveryMode === DeliveryModes.SELF_PICKUP;
}

export function getCptSlabBadgeColorScheme(normalizedCptSlab?: number): {
  bg: string;
  color: string;
} {
  switch (normalizedCptSlab) {
    case CPTSlabForManualOption.INSTANT_DELIVERY:
      return { bg: "orange.100", color: "orange.700" };
    case CPTSlabForManualOption.STANDARD_DELIVERY:
      return { bg: "orange.100", color: "orange.700" };
    default:
      return { bg: "orange.100", color: "orange.700" };
  }
}

export function shouldShowDeliveryOptionSlabBadge(
  option: DeliveryOptionSlabSource,
): boolean {
  return getCptSlabBadgeAbbreviation(option) != null;
}

export function normalizeDeliveryOption(
  option: DeliveryOptionInput,
): DeliveryOption {
  const normalizedSlab = normalizeCptSlabValue(option.cptSlab);

  switch (normalizedSlab) {
    case undefined:
      return option as DeliveryOption;
    default:
      return {
        ...option,
        cptSlab: normalizedSlab,
      };
  }
}

export function normalizeDeliveryOptions(
  options: DeliveryOptionInput[],
): DeliveryOption[] {
  return options.map(normalizeDeliveryOption);
}
